import React, { useEffect, useState, useMemo } from 'react';
import { Typography, ListItem, ListItemIcon, ListItemText, Box, CircularProgress } from '@mui/material';
import { Survey } from '../../../interfaces/survey.interface';
import { fetchSurvey } from '../../../services/surveys';
import { buildFullQuestionnaireObject, fetchDetailedQuestionnaire } from '../../../services/questionnaires';
import { initialBuildOutQuestionnaire } from '../../../services/buildQuestionnaire';
import { Questionnaire } from '../../../interfaces/questionnaire.interface';
import { Question } from '../../../interfaces/question.interface';
import { getEngagementReportCompany } from '../../../services/engagements';
import { Badge, Company, CompanyApplication, Contact } from '../../../interfaces/users.interface';
import { QuestionAnswer } from '../../../interfaces/questionAnswer.interface';
import moment from 'moment';
import { appTheme } from '../../Themes/light';
import { BestPractice } from '../../../interfaces/bestPractice.interface';
import { Answer } from '../../../interfaces/answer.interface';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useAppSelector } from '../../../app/hooks';
import { Topic } from '../../../interfaces/topic.interface';
import { WORKFLOW_MAP, isWorkflow531OrHigher } from '../../../services/workflow';
import { Workflow } from '../../../interfaces/workflow.interface';
import FileViewer from '../../FileViewer/FileViewer';
import { createEngagementAccessHistory } from '../../../services/engagements';
import  { getManyEvidence } from '../../../services/questionAnswer';
import { FULLY_IMPLEMENTED, NOT_APPLICABLE, NOT_IMPLEMENTED } from '../../../services/surveyHelpers';
import { BLUE_BADGE_IMAGE } from '../../../services/badge';
import { buildQuestionsFromDetailedQuestionnaire, generateConfidentialHeader, shouldSkipBp, surveyReportLegalLanguageSection } from '../../../services/reports';
import { REPORT_TYPES } from '../../../interfaces/reports.interface';
import { USER_TYPES } from '../../../services/user';

export default function BlueReport(props: {   engagementId: number | null, closeMenuItem: () => void, label: string, workflow: Workflow | null, surveyId?: number, siteAppName?: string | null }) {
  if (!props.workflow) return null;

  const { workflow, surveyId, closeMenuItem, engagementId } = props;

  const isWorkflow53OrGreater = useMemo(() => !['v5.1', 'v5.2'].includes(WORKFLOW_MAP[workflow.workflowType]?.version), [workflow]);
  const { user } = useAppSelector(state => state.user);
  const [reportLoading, setReportLoading] = useState(false);
  const [survey, setSurvey] = useState<Survey | undefined>(undefined);
  const [baseline, setBaseline] = useState<Survey | undefined>(undefined);
  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>(undefined);
  const [questions, setQuestions] = useState<Question[] | undefined>(undefined);
  const [domainRollUps, setDomainRollUp] = useState<any>(undefined);
  const [bpDisplay, setBPDisplay] = useState<any>(undefined);
  const [reportReady, setReportReady] = useState(false);
  const [site, setSite] = useState<Contact | undefined>(undefined);
  const [application, setApplication] = useState<CompanyApplication | undefined>(undefined);
  const [employeeCount, setEmployeeCount] = useState<Answer | undefined>(undefined);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showFileViewer, setShowFileViewer] = useState(false);

  (pdfMake as any).vfs = (pdfFonts as any).vfs;

  const badge = (workflow?.badges as Badge[])?.find(b => b.title === 'tpn_self_reported');

  const buildReport = async () => {
    if (!survey || !survey.questionnaire) {
      setReportLoading(false);
      return;
    }

    setReportLoading(true);
    const surQuestionnaire = survey.questionnaire;

    if (typeof surQuestionnaire === 'number') {
      console.error('Survey questionnaire is a number, expected object');
      setReportLoading(false);
      return;
    }

    getEngagementReportCompany(typeof survey.company === 'number' ? survey.company.toString() : survey.company.id!.toString()).then(comp => setCompany(comp));
    const questionnaireObjects = await buildFullQuestionnaireObject(surQuestionnaire.id);
    const builtQuestionnaire = initialBuildOutQuestionnaire({ ...questionnaireObjects, questionnaire: surQuestionnaire });
    setQuestionnaire(builtQuestionnaire.questionnaire);

    const detailedQuestionnaire = await fetchDetailedQuestionnaire(surQuestionnaire.id);
    const formattedQuestions = buildQuestionsFromDetailedQuestionnaire(
      detailedQuestionnaire, surQuestionnaire,
    );

    setQuestions(formattedQuestions);
  };

  const loopQuestionnaire = async () => {
    if (!survey || !company || !questionnaire || !questions || questions.length === 0) {
      setReportLoading(false);
      return;
    }

    const questionAnswers = survey.questionAnswers;

    if (!questionAnswers || questionAnswers.length === 0) {
      setReportLoading(false);
      return;
    }

    const rollups: any[] = [];
    const bps: any[] = [];

    for (const domain of questionnaire.domains) {
      const domainRollUp: any = { name: domain.title, bpCounts: { fi: 0, pi: 0, ni: 0, na: 0 }, igCounts: { fi: 0, pi: 0, ni: 0, na: 0 } };
      for (const topic of domain.topics) {
        for (const bp of topic.bestPractices) {
          const curBP = { bp, domain, topic, questions: ([] as any[]) };
          // Handle BP inclusions
          let skipBp = false;
          if (bp.inclusions?.length > 0) {
            const inclusionIds = bp.inclusions.map(i => (typeof i === 'number' ? i : i.id));
            skipBp = await shouldSkipBp(inclusionIds, survey.questionAnswers as QuestionAnswer[]);
          }
          for (const questionId of (bp.questions as unknown as number[])) {
            const question = questions.find(q => q.id === questionId);
            const answer = (survey.questionAnswers as QuestionAnswer[]).find(qa => qa.question === questionId);
            const qAnswer = question?.answers.find(ans => ans.id === answer?.answer?.[0]);
            let revealQuestion = !question?.inclusions.length || question.inclusions.some(({ answer: inclusionAnswer, question: inclusionQuestion }) => {
              const inclusionAnswerId = typeof inclusionAnswer === 'number' ? inclusionAnswer : inclusionAnswer.id;
              const inclusionQuestionId = typeof inclusionQuestion === 'number' ? inclusionQuestion : inclusionQuestion.id;
            
              const surveyQuestionAnswer = (survey.questionAnswers as QuestionAnswer[]).find(qa => qa.question === inclusionQuestionId);
              const baselineQuestionAnswer = (baseline?.questionAnswers as QuestionAnswer[])?.find(qa => qa.question === inclusionQuestionId);
            
              return (
                baselineQuestionAnswer?.answer?.includes(inclusionAnswerId) ||
                surveyQuestionAnswer?.answer?.includes(inclusionAnswerId) ||
                surveyQuestionAnswer?.compensatingControlAnswers?.includes(inclusionAnswerId)
              );
            });

            if (!question || !revealQuestion || (!revealQuestion && skipBp)) continue;

            // Fetch attachments if they exist
            let attachments: any[] = [];
            if (answer?.attachments && answer.attachments.length > 0) {
              try {
                attachments = await getManyEvidence(answer.attachments);
                answer.attachments = attachments;
              } catch (error) {
                console.error(`Error fetching evidence for question ${questionId}:`, error);
              }
            }

            curBP.questions.push({ question, answer, qAnswer });
          }
          if (curBP.questions.length > 0) bps.push(curBP);
        }
      }
      rollups.push(domainRollUp);
    }
    setDomainRollUp(rollups);
    setBPDisplay(bps);
    setReportLoading(false);
    setReportReady(true);
  };

  const setSiteOrApplication = () => {
    if (!company || !survey) return;

    const csite = survey.site ? company.locations.find(location => location.id === survey.site) : undefined;
    const capplication = survey.application ? company.companyApplications?.find(companyApplication => companyApplication.application.id === survey.application) : undefined;

    setSite(csite);
    setApplication(capplication);
  };

  useEffect(() => {
    if (!survey) return;
    buildReport();
  }, [survey]);

  useEffect(() => {
    if (!questions || !company || !questionnaire) return;
    loopQuestionnaire();
    setSiteOrApplication();
  }, [questions, company, questionnaire]);

  const fetchBaselineQuestions = async () => {
    if ((!site && !application) || !company || !baseline || typeof baseline.questionnaire === 'number') return;

    const detailedQuestionnaire = await fetchDetailedQuestionnaire(baseline.questionnaire.id);
    const formattedBaselineQuestions = buildQuestionsFromDetailedQuestionnaire(
      detailedQuestionnaire,
      baseline.questionnaire,
    );

    if (!formattedBaselineQuestions.length || !baseline.questionAnswers?.length) return;

    const employeeCountQuestion = formattedBaselineQuestions.find(question => question.title.toLowerCase().indexOf('number') !== -1 && question.title.toLowerCase().indexOf('employees') !== -1);
    if (employeeCountQuestion) {
      const questionAnswer = (baseline.questionAnswers as QuestionAnswer[]).find(qa => (qa.question as number) === employeeCountQuestion.id);
      if (questionAnswer && questionAnswer.answer?.length) setEmployeeCount(employeeCountQuestion.answers.find(answer => answer.id === questionAnswer.answer![0]));
    }
    if (bpDisplay) {
      setReportLoading(false);
      setReportReady(true);
    }
  };

  useEffect(() => {
    if ((!site && !application) || !company || !baseline) return;
    fetchBaselineQuestions();
  }, [baseline, site, application, company]);

  const requestBuildReport = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    if (reportLoading) return;
    if (!surveyId) {
      setReportLoading(false);
      return;
    }

    if (engagementId && user) {
      createEngagementAccessHistory({ engagement: engagementId, reportViewed: 'tpn_self_reported', accessedBy: user?.id! });
    }

    setReportLoading(true);
    fetchSurvey(surveyId).then(sur => setSurvey(sur));
    if (workflow?.id) {
      fetchSurvey(workflow.baseline!).then(bas => setBaseline(bas));
    }
  };

  const outputOverviewBox = () => {
    if (!survey || !company) return;
    const output = {
      margin: [0, 0, 0, 20],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [{
            border: [true, false, true, true],
            stack: [
              { text: [{ text: 'Survey Scope:', bold: true }, ` ${survey?.site ? 'Site' : application?.sites.length ? 'Site' : 'On-Prem'}`] },
              { text: [{ text: 'Survey Type:', bold: true }, ` ${survey?.site ? 'On Site' : 'Remote'}`] },
            ],
          }],
        ],
      },
    };

    if (site) output.table.body[0][0].stack.push({ text: [{ text: 'Related Facility:', bold: true }, ` ${site.name}`] });
    if ((application && application.sites?.length)) output.table.body[0][0].stack.push({ text: [{ text: 'Related Facilities:', bold: true }, ` ${application.sites.map(csite => csite.name).join(', ')}`] });
    if ((site && site.services?.length)) output.table.body[0][0].stack.push({ text: [{ text: 'Services:', bold: true }, ` ${site.services.map(service => service.name).join(', ')}`] });
    if ((application && application.services?.length)) output.table.body[0][0].stack.push({ text: [{ text: 'Services:', bold: true }, ` ${application.services.map(service => service.name).join(', ')}`] });
    if (employeeCount?.text) output.table.body[0][0].stack.push({ text: [{ text: 'Number of Employees:', bold: true }, ` ${employeeCount?.text}`] });
    const ownedApps = company?.companyApplications
      ?.filter(
        (ca) =>
          ca.application.company &&
          (typeof ca.application.company === 'number' && ca.application.company === company.id) ||
          (typeof ca.application.company !== 'number' && ca.application.company?.id === company.id),
      );
    if (site && ownedApps?.length) output.table.body[0][0].stack.push({ text: [{ text: 'Owned Applications:', bold: true }, ` ${ownedApps.map(app => app.application.name).join(', ')}`] });
    if (application) output.table.body[0][0].stack.push({ text: [{ text: 'Owned Application:', bold: true }, application.application.name ] });
    const thirdPartyApps = company?.companyApplications
      ?.filter(
        (ca) =>
          !ca.application.company ||
          (typeof ca.application.company === 'number' && ca.application.company !== company.id) ||
          (typeof ca.application.company !== 'number' && ca.application.company.id !== company.id),
      );
    if (site && thirdPartyApps?.length) output.table.body[0][0].stack.push({ text: [{ text: '3rd Party Licensed Applications:', bold: true }, ` ${thirdPartyApps.map(app => app.application.name).join(', ')}`] });

    return output;
  };

  const outputBPItems = () => {
    const bpItems:any = bpDisplay.map((bpitem: any, index: number) => {
      const item:any = [
        {
          style: 'headerBG',
          table: {
            widths: '*',
            body: [
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: `${bpitem.domain.code}: ${bpitem.domain.title} - ${bpitem.topic.title}`,
              } ],
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: `${bpitem.domain.topics.findIndex((top: Topic) => bpitem.topic.id === top.id) + 1}.${bpitem.topic.bestPractices.findIndex((bp: BestPractice) => bpitem.bp.id === bp.id)} ${bpitem.bp.title}`,
              }],
            ],
          },
        },
      ];
      bpitem.questions.map((qContain: any) => {
        const fullyImplementedAnswers = qContain.question.answers.filter((answer: Answer) => ![NOT_APPLICABLE, NOT_IMPLEMENTED].includes(answer.text || ''));
        const fullyImplementedAnswerOption = qContain.question.answers.find((answer: Answer) => answer.text === FULLY_IMPLEMENTED);

        let isFullyImplemented: boolean = false;

        if (qContain.question.type === 'single_select') {
          const yesAnswer = qContain.question.answers.find((a: Answer) => a.text === 'Yes');
          isFullyImplemented = qContain.answer?.answer?.length > 0 && qContain.answer.answer?.includes(yesAnswer?.id);
        } else if (isWorkflow53OrGreater) {
          isFullyImplemented  = qContain.answer?.answer?.length === fullyImplementedAnswers.length;
        } else {
          isFullyImplemented = qContain.answer?.answer?.length > 0 && qContain.answer.answer?.includes(fullyImplementedAnswerOption?.id);
        }

        const hasNoAnswer = !qContain.answer?.answer?.length
          && !qContain.answer?.compensatingControlAnswers?.length
          && !qContain.answer?.nonApplicableAnswers?.length
          &&  !qContain.answer?.compensatingControlAnswers?.length;

        const notImplementedAnswer = qContain.question.answers.find((a: Answer) => a.text === NOT_IMPLEMENTED || a.text === 'No');
        const notApplicableAnswer = qContain.question.answers.find((a: Answer) => a.text === NOT_APPLICABLE);
        const answerIsNotImplemented = qContain.answer?.answer.includes(notImplementedAnswer?.id);

        const getFileName = (url: string): string => {
          try {
            return new URL(url).pathname.split('/').pop()!;
          } catch {
            return url.split('?')[0].split('/').pop()!;
          }
        };
        const attachmentText = qContain.answer?.attachments
          ?.map((attachment: any) => {
            const fileName = getFileName(attachment.file.document);
            return `${fileName} (${attachment.isPublic ? 'Public' : 'Private'})`;
          })
          .join(', ');

        const questionArray: any = [
          qContain.question.isBestPractice
            ? {
              columns: [
                {
                  table: {
                    widths: [16],
                    body: [
                      [
                        {
                          image: 'WorkspacePremium',
                          width: 16,
                          margin: [0, 1, 5, 0],
                          valign: 'top',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                  width: 21,
                },
                {
                  fontSize: 16,
                  text: qContain.question.title,
                  margin: [0, 5, 0, 5],
                  bold: true,
                },
              ],
            }
            : {
              fontSize: 16,
              text: qContain.question.title,
              margin: [0, 5, 0, 5],
              bold: true,
            },
          {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    text: isFullyImplemented
                      ? FULLY_IMPLEMENTED
                      : answerIsNotImplemented
                        ? NOT_IMPLEMENTED
                        : qContain.answer?.answer.includes(notApplicableAnswer?.id)
                          ? NOT_APPLICABLE
                          : (hasNoAnswer || qContain?.question?.type === 'single_select')
                            ? ''
                            : 'Partially Implemented',
                    bold: true,
                    fillColor: isFullyImplemented
                      ? appTheme.palette.success.main
                      : answerIsNotImplemented
                        ? appTheme.palette.error.main
                        : qContain?.answer?.answer.includes(notApplicableAnswer?.id)
                          ? '#ccc'
                          : appTheme.palette.nonCompliant.main,
                    color: isFullyImplemented || answerIsNotImplemented || hasNoAnswer ? 'white' : 'black',
                    margin: [10, 5, 10, 5],
                  },
                ],
              ],
            },
            layout: 'noBorders',
            margin: [0, 0, 5, 10],
          },
          {
            table: {
              widths: isWorkflow53OrGreater
                ? ['*', 'auto', 'auto', 'auto', 'auto']
                : ['*', 'auto'],
              body: [
                [
                  { text: '' },
                  { text: 'YES', alignment: 'center', noWrap: true },
                  ...(isWorkflow53OrGreater
                    ? [
                      { text: 'C/C', alignment: 'center', noWrap: true },
                      { text: 'N/I', alignment: 'center', noWrap: true },
                      { text: 'N/A', alignment: 'center', noWrap: true },
                    ]
                    : []),
                ],
                ...qContain.question.answers
                  .filter((a: Answer) => isWorkflow53OrGreater ? ![qContain?.question?.type === 'single_select' ? '' : NOT_APPLICABLE, NOT_IMPLEMENTED, FULLY_IMPLEMENTED].includes(a.text || '') : true)
                  .map((answer: Answer) => {
                    const answered = qContain.answer && qContain.answer.answer.findIndex((ans: number) => ans === answer.id) > -1;
                    const answeredCC = qContain.answer && qContain.answer.compensatingControlAnswers.findIndex((ans: number) => ans === answer.id) > -1;
                    const answeredNI = qContain.answer && qContain.answer.nonImplementedAnswers.findIndex((ans: number) => ans === answer.id) > -1;
                    const answeredNA = qContain.answer && qContain.answer.nonApplicableAnswers.findIndex((ans: number) => ans === answer.id) > -1;

                    const onlyYesAnswer =
                      answer.text === FULLY_IMPLEMENTED ||
                      answer.text === NOT_IMPLEMENTED ||
                      answer.text === NOT_APPLICABLE;

                    return [
                      { text: answer.text },
                      { text: answered ? 'X' : '', alignment: 'center' },
                      ...(isWorkflow53OrGreater
                        ? [
                          {
                            text: onlyYesAnswer ? '-' : (answeredCC ? 'X' : ''),
                            alignment: 'center',
                            color: onlyYesAnswer ? 'grey' : 'black',
                            fillColor: onlyYesAnswer ? '#d3d3d3' : undefined,
                          },
                          {
                            text: onlyYesAnswer ? '-' : (answeredNI ? 'X' : ''),
                            alignment: 'center',
                            color: onlyYesAnswer ? 'grey' : 'black',
                            fillColor: onlyYesAnswer ? '#d3d3d3' : undefined,
                          },
                          {
                            text: onlyYesAnswer ? '-' : (answeredNA ? 'X' : ''),
                            alignment: 'center',
                            color: onlyYesAnswer ? 'grey' : 'black',
                            fillColor: onlyYesAnswer ? '#d3d3d3' : undefined,
                          },
                        ]
                        : []),
                    ];
                  }),
              ],
            },
            margin: [0, 0, 10, 10],
          },
          qContain.answer?.comment
            ? {
              fontSize: 12,
              text: [
                { text: 'Additional Details: ', bold: true },
                { text: qContain.answer.comment },
              ],
              margin: [0, 0, 0, 5],
            }
            : {},
          qContain.answer?.attachments?.length > 0
            ? {
              fontSize: 12,
              lineHeight: 1.2,
              text: [
                { text: 'Attachments: ', bold: true },
                { text: attachmentText },
              ],
              margin: [0, 0, 0, 0],
            }
            : {},
        ];

        item.push({
          table: {
            headerRows: 0,
            widths: ['*'],
            body: [
              [{
                stack: questionArray,
              }],
            ],
          },
          layout: 'noBorders',
          margin: [0, 10, 0, 0],
        });
      });
      if (index + 1 !== bpDisplay.length) return { stack: item, margin: [0, 0, 0, 10] };
      return { stack: item };
    });

    return bpItems;
  };
  
  const generatePDF = async () => {
    if (!(reportReady && bpDisplay && domainRollUps && company && questionnaire && user)) return;

    const curDate = moment();

    pdfMake.fonts = {
      Roboto: {
        normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
      },
      MaterialSymbolsOutlined: {
        normal: 'https://fonts.gstatic.com/s/materialsymbolsoutlined/v75/kJF1BvYX7BgnkSrUwT8OhrdQw4oELdPIeeII9v6oDMzByHX9rA6RzaxHMPdY43zj-jCxv3fzvRNU22ZXGJpEpjC_1n-q_4MrImHCIJIZrDCvHOej.woff2',
      },
    };

    const docDefinition: any = {
      content: [
        {
          stack: [
            { image: isWorkflow531OrHigher(workflow.workflowType) ? 'TPN_NEXT_GEN' : 'TPN', fit: [400, 400], margin: [0, 130, 0, 20] },
            { text: 'BLUE SELF-REPORTED QUESTIONNAIRE', fontSize: 25 },
            ...(site ?
              [
                {
                  stack: [
                    company.name,
                    site.name,
                    site.address,
                    site.address2,
                    site.address3,
                    `${site.city}, ${site.state} ${site.zipcode}`,
                    site.country,
                  ], fontSize: 16, margin: [0, 30, 0, 20],
                },
                { text: 'Primary Contact Information', fontSize: 18 },
                `${site.primaryContact?.firstName} ${site.primaryContact?.lastName}`,
                site.primaryContact?.email,
                site.primaryContact?.contact?.phoneNumber,
              ]
              :
              [
                {
                  stack: [
                    company.name,
                    company.contact?.address,
                    company.contact?.address2,
                    company.contact?.address3,
                    `${company.contact?.city}, ${company.contact?.state} ${company.contact?.zipcode}`,
                    company.contact?.country,
                  ], fontSize: 16, margin: [0, 30, 0, 20],
                },
                { text: 'Primary Contact Information', fontSize: 18 },
                `${company.primaryContactUser?.firstName} ${company.primaryContactUser?.lastName}`,
                company.primaryContactUser?.email,
                company.primaryContactUser?.contact?.phoneNumber,
              ]
            ),
            {
              stack: [
                `Report Generated: ${moment().format('MMM DD, YYYY')}`,
                `Questionnaire Completed: ${moment(badge?.createdAt).format('MMM DD, YYYY')}`,
                `Expiration Date: ${moment(badge?.expirationDate).format('MMM DD, YYYY')}`,
              ], fontSize: 12, margin: [0, 40, 0, 0],
            },
            { text: questionnaire.title, margin: [0, 50, 0, 0] },
          ], alignment: 'center', pageBreak: 'after',
        },
        surveyReportLegalLanguageSection(
          moment(badge?.createdAt).format('MMM DD, YYYY'),
          company.name,
          questionnaire,
          appTheme,
        ),
        {
          style: 'headerBG',
          table: {
            widths: '*',
            body: [
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: 'Overview',
              }],
            ],
          },
        },
        outputOverviewBox(),
        {
          stack: outputBPItems(),
        },
      ],
      images: {
        TPN: `${window.location.origin}/assets/TPN_BlueLogoTransparent_1280wide.png`,
        TPN_NEXT_GEN: `${window.location.origin}/assets/TPN_NextGen_BlueLogoTransparent_1280wide.png`,
        WorkspacePremium: `${window.location.origin}/assets/Workspace-Premium.png`,
      },
      styles: {
        headerBG: {
          fontSize: 16,
          color: 'white',
        },
      },
      defaultStyle: {
        font: 'Roboto',
      },
      info: {
        title: `TPN-Best_Practice_Questionnaire-${encodeURIComponent(company.name.replace(/\s+/g, '-'))}-${curDate.format('YYYY-MM-DD')}`,
        author: 'Trusted Partner Network',
      },
      watermark: { text: `TPN Best Practice Questionnaire - ${user.email} - ${curDate.format('MMMM DD YYYY')}`, color: 'blue', opacity: 0.2, bold: true, italics: false },
      footer: function (currentPage: number, pageCount: number) { return { text: currentPage.toString() + ' of ' + pageCount, alignment: 'center', margin: [0, 10, 0, 0 ] }; },
      header: generateConfidentialHeader(
        company.name,
        curDate.format('MMMM DD YYYY'),
        REPORT_TYPES.SURVEY,
      ),
    };
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);

    pdfDocGenerator.getBlob((blob) => {
      const localPdfUrl = URL.createObjectURL(blob);
      setPdfUrl(localPdfUrl);
      setShowFileViewer(true);
    });
  };

  useEffect(() => {
    if (!reportReady) return;

    setShowFileViewer(true);
    generatePDF();
  }, [reportReady]);

  return (
    <Box>
      {(!surveyId) && <Typography>Could not find questionnaire</Typography>}
  
      {surveyId && (
        <ListItem 
          button 
          onClick={(e) => { requestBuildReport(e); }}
          sx={{ 
            px: 0, 
            '&:hover': { backgroundColor: 'transparent !important' }, 
            '&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
            '&:active': { backgroundColor: 'transparent !important' },
            '&.MuiListItem-root': {
              '&:hover, &:focus, &:active': {
                backgroundColor: 'transparent !important',
              },
            },
            transition: 'none',
            '& .MuiTouchRipple-root': { display: 'none' }, 
          }}
        >
          <ListItemIcon>
          {!reportLoading ? (
            <img
              src={BLUE_BADGE_IMAGE}
              alt="Blue Badge"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '1px solid #ddd',
                overflow: 'visible',
                backgroundColor: '#fff',
              }}
              />
          ) : <CircularProgress size={24} />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">Questionnaire Report</Typography>
              </Box>
            }
          />
        </ListItem>
      )}
  
      {pdfUrl && showFileViewer && (
        <FileViewer
          canDownload={USER_TYPES.ASSESSOR === user?.type}
          previewFile={pdfUrl}
          closeFileViewer={() => {
            setShowFileViewer(false);
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
            closeMenuItem();
          }}
          showFileViewer={showFileViewer}
        />
      )}
    </Box>
  );
}